// Shared read/write layer for the /products (and /cp/...) listing filters.
//
// Every filter in BooksView.jsx's sidebar lives in one of two places:
//   - the localStorage keys it has always used (categories, subCategories,
//     subSubCategories, parentCategories, publishers, collections,
//     multiproductids, stock, min_price, max_price)
//   - the Redux `searchData` slice (collaborators, and the special toggle
//     filters: favorites, newarrival, bestsellers, is_selected, alsosee,
//     upcoming)
// Neither changes here — this module only keeps the URL query string in
// sync with both, so a copied /products link reproduces the exact same
// filtered view for anyone who opens it, instead of landing on an
// unfiltered page.

// URL query param name -> localStorage key, for the array-valued filters.
const LOCAL_ARRAY_FIELDS = {
  cat: "categories",
  parentcat: "parentCategories",
  subcat: "subCategories",
  subsubcat: "subSubCategories",
  publisher: "publishers",
  collection: "collections",
  attr: "multiproductids",
};

// URL query param name -> localStorage key, for the scalar filters.
const LOCAL_SCALAR_FIELDS = {
  stock: "stock",
  min_price: "min_price",
  max_price: "max_price",
};

// URL query param name -> searchData key, for the array-valued filter that
// lives in Redux instead of localStorage.
const SEARCH_DATA_ARRAY_FIELDS = {
  collab: "collaborators",
};

// URL query param name -> searchData key, for the boolean "special filter"
// checkboxes (all live in Redux, presence-based: param present = true).
const SEARCH_DATA_FLAG_FIELDS = {
  favorites: "favorites",
  newarrival: "newarrival",
  bestsellers: "bestsellers",
  is_selected: "is_selected",
  alsosee: "alsosee",
  upcoming: "upcoming",
};

export const FILTER_URL_KEYS = [
  ...Object.keys(LOCAL_ARRAY_FIELDS),
  ...Object.keys(LOCAL_SCALAR_FIELDS),
  ...Object.keys(SEARCH_DATA_ARRAY_FIELDS),
  ...Object.keys(SEARCH_DATA_FLAG_FIELDS),
];

// Every existing handler in BooksView.jsx stores these ids as numbers (e.g.
// parent.id straight from the API), and compares them with strict equality
// (`col.id === id`, `.includes(categoryID)`) — URLSearchParams always yields
// strings, so ids read from the URL need converting back to numbers or every
// one of those comparisons silently fails.
const toStoredId = (value) => {
  const asNumber = Number(value);
  return Number.isNaN(asNumber) ? value : asNumber;
};

// URLSearchParams -> { categories: [...], stock: '...', favorites: true, ... }
// (keyed by the internal storage/searchData field name, not the URL param name)
export function parseFiltersFromSearchParams(searchParams) {
  const filters = {};
  for (const [urlKey, fieldKey] of Object.entries(LOCAL_ARRAY_FIELDS)) {
    const values = searchParams.getAll(urlKey).map(toStoredId);
    const deduped = [...new Set(values)];
    if (deduped.length > 0) filters[fieldKey] = deduped;
  }
  for (const [urlKey, fieldKey] of Object.entries(LOCAL_SCALAR_FIELDS)) {
    const value = searchParams.get(urlKey);
    if (value) filters[fieldKey] = value;
  }
  for (const [urlKey, fieldKey] of Object.entries(SEARCH_DATA_ARRAY_FIELDS)) {
    const values = searchParams.getAll(urlKey).map(toStoredId);
    const deduped = [...new Set(values)];
    if (deduped.length > 0) filters[fieldKey] = deduped;
  }
  for (const [urlKey, fieldKey] of Object.entries(SEARCH_DATA_FLAG_FIELDS)) {
    if (searchParams.has(urlKey)) filters[fieldKey] = true;
  }
  return filters;
}

// { categories: [...], stock: '...', favorites: true, ... } -> URLSearchParams,
// empty/falsy values omitted.
export function buildSearchParams(filters = {}) {
  const params = new URLSearchParams();
  for (const [urlKey, fieldKey] of [
    ...Object.entries(LOCAL_ARRAY_FIELDS),
    ...Object.entries(SEARCH_DATA_ARRAY_FIELDS),
  ]) {
    const values = filters[fieldKey];
    if (Array.isArray(values)) {
      const deduped = [...new Set(values)];
      deduped.forEach((value) => {
        if (value !== null && value !== undefined && value !== "") {
          params.append(urlKey, value);
        }
      });
    }
  }
  for (const [urlKey, fieldKey] of Object.entries(LOCAL_SCALAR_FIELDS)) {
    const value = filters[fieldKey];
    if (value !== null && value !== undefined && value !== "") {
      params.set(urlKey, value);
    }
  }
  for (const [urlKey, fieldKey] of Object.entries(SEARCH_DATA_FLAG_FIELDS)) {
    if (filters[fieldKey]) params.set(urlKey, "1");
  }
  return params;
}

// Ready-to-use string for navigate() — e.g. buildFilterUrl('/products', { publishers: ['12'] })
export function buildFilterUrl(pathname, filters = {}) {
  const search = buildSearchParams(filters).toString();
  return search ? `${pathname}?${search}` : pathname;
}

export function hasAnyFilter(filters = {}) {
  return Object.values(filters).some((value) =>
    Array.isArray(value) ? value.length > 0 : Boolean(value)
  );
}

// Reads the current filter state directly out of the localStorage keys
// BooksView.jsx already writes to (the searchData-backed fields —
// collaborators and the special toggle filters — aren't localStorage-backed,
// so BooksView.jsx reads/writes those from Redux directly; see
// readSearchDataFilters/searchDataFilterKeys below).
export function readFiltersFromLocalStorage() {
  const filters = {};
  for (const fieldKey of Object.values(LOCAL_ARRAY_FIELDS)) {
    const raw = localStorage.getItem(fieldKey);
    if (!raw) continue;
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) filters[fieldKey] = parsed;
    } catch {
      // Ignore malformed localStorage content.
    }
  }
  for (const fieldKey of Object.values(LOCAL_SCALAR_FIELDS)) {
    const value = localStorage.getItem(fieldKey);
    if (value) filters[fieldKey] = value;
  }
  return filters;
}

// Writes a filters object into the localStorage keys, clearing whichever
// keys aren't present in `filters` so stale values don't linger.
export function writeFiltersToLocalStorage(filters = {}) {
  for (const fieldKey of Object.values(LOCAL_ARRAY_FIELDS)) {
    const value = filters[fieldKey];
    if (Array.isArray(value) && value.length > 0) {
      localStorage.setItem(fieldKey, JSON.stringify(value));
    } else {
      localStorage.removeItem(fieldKey);
    }
  }
  for (const fieldKey of Object.values(LOCAL_SCALAR_FIELDS)) {
    const value = filters[fieldKey];
    if (value) {
      localStorage.setItem(fieldKey, value);
    } else {
      localStorage.removeItem(fieldKey);
    }
  }
}

// Reads the searchData-backed filters (collaborators + the special toggle
// filters) out of a searchData[0]-shaped object, into the same filters
// shape everything above uses. Handles the two fields that already have two
// accepted spellings in searchData (bestsellers/bestseller, is_selected/isSelected).
export function readSearchDataFilters(searchDataItem = {}) {
  const filters = {};
  const collaborators = searchDataItem?.collaborators;
  if (Array.isArray(collaborators) && collaborators.length > 0) {
    filters.collaborators = collaborators;
  }
  if (searchDataItem?.favorites === true) filters.favorites = true;
  if (searchDataItem?.newarrival === true) filters.newarrival = true;
  if (searchDataItem?.bestsellers === true || searchDataItem?.bestseller === true) {
    filters.bestsellers = true;
  }
  if (searchDataItem?.is_selected === true || searchDataItem?.isSelected === true) {
    filters.is_selected = true;
  }
  if (searchDataItem?.alsosee === true) filters.alsosee = true;
  if (searchDataItem?.upcoming === true) filters.upcoming = true;
  return filters;
}

export const searchDataFilterKeys = [
  ...Object.values(SEARCH_DATA_ARRAY_FIELDS),
  ...Object.values(SEARCH_DATA_FLAG_FIELDS),
];
